// Custom tagline sayings shown on the user's dashboard hero tile.
// Stored at users/{uid}/customisations/customSayings as { sayings: string[] }.

import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { COLLECTIONS } from '../constants/collections';

const CUSTOMISATIONS_SUB = 'customisations';
const CUSTOM_SAYINGS_DOC = 'customSayings';
export const CUSTOM_SAYINGS_SLOT_COUNT = 10;

function docRef(uid: string) {
  return doc(db, COLLECTIONS.USERS, uid, CUSTOMISATIONS_SUB, CUSTOM_SAYINGS_DOC);
}

function coerceArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (let i = 0; i < Math.min(raw.length, CUSTOM_SAYINGS_SLOT_COUNT); i++) {
    const v = raw[i];
    out.push(typeof v === 'string' ? v : '');
  }
  while (out.length < CUSTOM_SAYINGS_SLOT_COUNT) out.push('');
  return out;
}

/** One-shot fetch. Returns a stable length-10 array; empty slots are ''. */
export async function loadCustomSayings(uid: string): Promise<string[]> {
  try {
    const snap = await getDoc(docRef(uid));
    if (!snap.exists()) return Array(CUSTOM_SAYINGS_SLOT_COUNT).fill('');
    const data = snap.data() as { sayings?: unknown } | undefined;
    return coerceArray(data?.sayings);
  } catch (err) {
    console.warn('loadCustomSayings failed:', err);
    return Array(CUSTOM_SAYINGS_SLOT_COUNT).fill('');
  }
}

/** Write the full 10-slot array. Blank slots are preserved as empty strings. */
export async function saveCustomSayings(
  uid: string,
  sayings: string[],
): Promise<void> {
  const normalized = coerceArray(sayings);
  await setDoc(
    docRef(uid),
    { sayings: normalized, updatedAt: new Date().toISOString() },
    { merge: true },
  );
}

/** Live listener for cross-device sync. Calls cb with a length-10 array. */
export function subscribeToCustomSayings(
  uid: string,
  cb: (sayings: string[]) => void,
): () => void {
  return onSnapshot(
    docRef(uid),
    (snap) => {
      if (!snap.exists()) {
        cb(Array(CUSTOM_SAYINGS_SLOT_COUNT).fill(''));
        return;
      }
      const data = snap.data() as { sayings?: unknown } | undefined;
      cb(coerceArray(data?.sayings));
    },
    (err) => {
      console.warn('customSayings subscription error:', err);
    },
  );
}
